import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { getBuffPrice, getRowImage, buildStickerIndex, withStickerImages } from './helpers';
import { openLends, closedLends, daysOut, daysUntilDue, isOverdue, lentValue, LEND_COLOR } from './lending';
import { rowName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';
import StickerSlots from './StickerSlots';
import './LendingTab.css';

// Lending — what is out with friends right now.
//
// The figures here deliberately do NOT feed profit. A lend realizes nothing:
// the item is still yours and still counted in every portfolio total, which is
// why "value on loan" is framed as a slice of what you already hold rather than
// as money owed to you.

const FILTERS = [
  { key: 'open',     label: 'Out now' },
  { key: 'overdue',  label: 'Overdue' },
  { key: 'returned', label: 'Came back' },
];

const fmtDate = iso => iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : 'n/a';

// Identifies a loan across the two passes below — the one that asks the archive
// for prices and the one that renders. Not the array index: the render loop
// walks a filtered list, so its indices do not line up with the request list's.
const lendKey = e => e.lend.id || `${e.realIndex}_${e.lend.at}`;

export default function LendingTab({
  rows, settings, skinsData = [], livePrices, csfloatPrices, inventoryMap, syncedInventory = [],
  onLendItems, onMarkReturned, onWriteOff,
}) {
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('open');
  const [detail, setDetail] = useState(null);

  const open = useMemo(
    () => openLends({ rows, inventoryItems: syncedInventory }),
    [rows, syncedInventory],
  );
  const closed = useMemo(() => closedLends(rows), [rows]);

  // ── What the market did while it was gone ─────────────────
  // The row's own snapshots only start the day it was added, so a loan made
  // before that — or backdated to one — had no baseline and read n/a forever.
  // The archive prices the actual day it went out, back to October 2023.
  //
  // A closed loan is measured to the day it came back rather than to today:
  // what happened to the market after you had the item back is not part of the
  // loan.
  const lendPoints = useMemo(() => [...open, ...closed].map(e => ({
    key:  lendKey(e),
    name: rowName(e.row),
    date: dayOf(e.lend.at),
    to:   dayOf(e.lend.returnedAt || e.lend.writtenOffAt),
  })), [open, closed]);
  const archive = useSinceMoves(lendPoints, csfloatPrices);

  // Rows rebuilt from the trade history carry sticker names but no pictures.
  // Built once here rather than per card, since the database is 40k entries.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  const totals = useMemo(() => {
    const value = open.reduce((s, e) => s + (getBuffPrice(livePrices, e.row) ?? parseFloat(e.row.buyPrice) ?? 0), 0);
    const cost  = open.reduce((s, e) => s + (parseFloat(e.row.buyPrice) || 0), 0);
    const people = new Set(open.map(e => e.lend.to.steamId64 || e.lend.to.name.toLowerCase()));

    // Drift across the loans that have both a price from the day they went out
    // and one today. Items missing either are left out entirely rather than
    // counted at zero, which would quietly dilute the figure.
    //
    // The archive is asked first because it can price the actual day the item
    // left; the old path — whatever the row had snapshotted — only covers loans
    // made after the row was created, and is kept for the case where the
    // archive has never heard of the item.
    const drift = open.reduce((acc, e) => {
      const mv = archive.get(lendKey(e));
      const then = mv ? mv.then : lentValue(e.row, e.lend);
      const now  = mv ? mv.now  : getBuffPrice(livePrices, e.row);
      if (then == null || now == null) return acc;
      return {
        change: acc.change + (now - then),
        base: acc.base + then,
        n: acc.n + 1,
        fromArchive: acc.fromArchive + (mv ? 1 : 0),
      };
    }, { change: 0, base: 0, n: 0, fromArchive: 0 });

    return {
      value, cost, drift,
      driftPct: drift.base > 0 ? (drift.change / drift.base) * 100 : null,
      people:  people.size,
      overdue: open.filter(e => isOverdue(e.lend)).length,
      back:    open.filter(e => e.back).length,
      longest: open.reduce((m, e) => Math.max(m, daysOut(e.lend)), 0),
    };
  }, [open, livePrices, archive]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = e =>
      !q ||
      e.row.skin?.value?.toLowerCase().includes(q) ||
      e.lend.to.name.toLowerCase().includes(q);
    if (filter === 'returned') return closed.filter(match);
    if (filter === 'overdue')  return open.filter(e => isOverdue(e.lend)).filter(match);
    return open.filter(match);
  }, [open, closed, filter, query]);

  const profC = settings?.profitTextColor || 'var(--green)';

  if (!open.length && !closed.length) {
    return (
      <div className="lnd-root">
        <div className="lv2-empty">
          Nothing is out on loan.
          <div style={{ marginTop: 16 }}>
            <button className="lnd-btn primary" onClick={() => onLendItems && onLendItems()}>
              Lend an item
            </button>
          </div>
          <p style={{ maxWidth: 460, margin: '18px auto 0', fontSize: '0.95rem', lineHeight: 1.6 }}>
            Lending an item keeps it in your totals: the cost basis stays on the row and the
            market value still counts toward your portfolio. Only its whereabouts change.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lnd-root">

      {/* ── Headline totals ────────────────────────── */}
      <div className="pt-hero">
        <div className="pt-hero-cell">
          <div className="k">Value on loan</div>
          <div className="v" style={{ color: LEND_COLOR }}>${totals.value.toFixed(2)}</div>
          <div className="s">
            {totals.drift.n > 0 ? (
              <>
                <span style={{ color: totals.drift.change >= 0 ? profC : 'var(--red)', fontWeight: 700 }}>
                  {totals.drift.change >= 0 ? '+' : '-'}${Math.abs(totals.drift.change).toFixed(2)}
                  {totals.driftPct != null ? ` (${totals.driftPct >= 0 ? '+' : ''}${totals.driftPct.toFixed(1)}%)` : ''}
                </span>
                {' '}since these went out
                {totals.drift.n < open.length ? ` · ${totals.drift.n} of ${open.length} priced` : ''}
                {totals.drift.fromArchive > 0 ? ' · from CSFloat history' : ''}
              </>
            ) : 'still counted in your portfolio, just not in your inventory'}
          </div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Items out</div>
          <div className="v">{open.length}</div>
          <div className="s">
            with {totals.people} {totals.people === 1 ? 'person' : 'people'}
            {totals.longest > 0 ? ` · longest ${totals.longest}d` : ''}
          </div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Needs a look</div>
          <div className="v" style={{ color: totals.overdue > 0 ? 'var(--red)' : totals.back > 0 ? profC : 'var(--text)' }}>
            {totals.overdue + totals.back}
          </div>
          <div className="s">
            {totals.overdue > 0 ? `${totals.overdue} overdue` : 'none overdue'}
            {totals.back > 0 ? ` · ${totals.back} back in your inventory` : ''}
          </div>
        </div>
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ position: 'static', marginBottom: 16 }}>
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search item or friend…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        {FILTERS.map(f => (
          <button key={f.key} className={`lv2-chip ${filter === f.key ? 'on' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}
            {f.key === 'overdue' && totals.overdue > 0 ? ` (${totals.overdue})` : ''}
          </button>
        ))}
        <button className="lnd-btn primary" style={{ marginLeft: 'auto' }}
          onClick={() => onLendItems && onLendItems()}>
          Lend an item
        </button>
      </div>

      {/* ── Loans ──────────────────────────────────── */}
      {visible.length === 0 && (
        <div className="lv2-empty">
          {filter === 'returned' ? 'Nothing has come back yet.'
            : filter === 'overdue' ? 'Nothing is overdue.'
            : 'No loans match your search.'}
        </div>
      )}

      {visible.map((e, i) => {
        const img  = getRowImage(e.row, skinsData, inventoryMap);
        const mkt  = getBuffPrice(livePrices, e.row);
        const cost = parseFloat(e.row.buyPrice) || 0;
        // What it was worth when it went out, against what it is worth now.
        // A lend realizes nothing, so this is drift on an item you still own,
        // not a gain — it is here to answer "should I be chasing this back?".
        //
        // Three sources, in order of how much they know:
        //   archive  the CSFloat reading nearest the day it left, compared
        //            against CSFloat today — works for any loan, any date
        //   row      what the row itself had snapshotted, Buff against Buff
        //   neither  no figure, and the caption says which end was missing
        const mv   = archive.get(lendKey(e));
        const then = mv ? mv.then : lentValue(e.row, e.lend);
        const nowV = mv ? mv.now : mkt;
        const move = then != null && nowV != null ? nowV - then : null;
        const movePct = move != null && then > 0 ? (move / then) * 100 : null;
        const done = filter === 'returned';
        const over = !done && isOverdue(e.lend);
        const due  = daysUntilDue(e.lend);
        return (
          <motion.div key={e.lend.id || `${e.realIndex}_${i}`}
            className={`lnd-card${over ? ' over' : ''}${done ? ' done' : ''}`}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 10) * 0.04 }}>

            <div className="lnd-card-item idm-clickable"
              role="button" tabIndex={0} title="Open details"
              onClick={() => setDetail({ row: e.row, img, lend: e.lend, then })}
              onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setDetail({ row: e.row, img, lend: e.lend, then }); } }}>
              {img ? <img src={img} alt="" /> : <span className="lnd-card-blank" />}
              <div style={{ minWidth: 0 }}>
                <div className="lnd-card-name">{e.row.skin?.value}</div>
                <div className="lnd-card-sub">
                  {e.row.condition?.value || 'n/a'}
                  {e.row.float != null ? ` · ${Number(e.row.float).toFixed(6)}` : ''}
                </div>
                {/* Drawn on every card, empty or not, at full size. A strip that
                    only appeared on crafted items made those cards taller than
                    the rest; reserving the row keeps every card one height. */}
                <StickerSlots
                  stickers={withStickerImages(e.row.stickers, stickerIndex)}
                  charms={e.row.charms}
                  prices={livePrices} variant="classic"
                  hideWhenEmpty={false}
                  blank={!(e.row.stickers || []).some(s => s?.name)}
                  style={{ marginTop: 6 }} />
              </div>
            </div>

            <div className="lnd-card-who">
              <div className="k">{done ? 'Was with' : 'With'}</div>
              <div className="v" title={e.lend.to.name}>
                {e.lend.to.profileUrl
                  ? <a href={e.lend.to.profileUrl} target="_blank" rel="noreferrer">{e.lend.to.name}</a>
                  : e.lend.to.name}
              </div>
              <div className="s">
                {done
                  ? e.lend.writtenOffAt
                    ? `written off ${fmtDate(e.lend.writtenOffAt)}`
                    : `returned ${fmtDate(e.lend.returnedAt)}`
                  : `since ${fmtDate(e.lend.at)} · ${daysOut(e.lend)} day${daysOut(e.lend) !== 1 ? 's' : ''}`}
              </div>
              {e.lend.note && <div className="lnd-card-note" title={e.lend.note}>{e.lend.note}</div>}
            </div>

            <div className="lnd-card-money">
              <div>
                <div className="k">Cost</div>
                <div className="v">${cost.toFixed(2)}</div>
              </div>
              <div>
                <div className="k">Market</div>
                <div className="v">{mkt != null ? `$${mkt.toFixed(2)}` : 'n/a'}</div>
              </div>
              <div>
                <div className="k">{done ? 'While it was out' : 'Since lent'}</div>
                <div className="v" style={move == null ? undefined : { color: move >= 0 ? profC : 'var(--red)' }}>
                  {move == null ? 'n/a' : `${move >= 0 ? '+' : '-'}$${Math.abs(move).toFixed(2)}`}
                </div>
                <div className="s">
                  {movePct == null
                    ? then == null ? 'no price when lent' : 'no live price'
                    : `${movePct >= 0 ? '+' : ''}${movePct.toFixed(1)}% · was $${then.toFixed(2)}`}
                </div>
                {/* Where the "was" came from. The archive is sparse before mid-2025,
                    so a loan dated then is often priced off the nearest recorded
                    day rather than the day itself — which has to be on the card,
                    not buried in a tooltip. */}
                {mv && (
                  <div className="s" style={{ opacity: 0.72 }}>
                    {readingNote(mv)}
                  </div>
                )}
              </div>
            </div>

            {!done && (
              <div className="lnd-card-act">
                <div className="lnd-card-flags">
                  {/* The strongest thing this page can say. A float match means the
                      item is provably back in the inventory, so the button is an
                      acknowledgement rather than a question. */}
                  {/* The flag cell is narrow enough that the longer wordings
                      ellipsize, so each one also carries its full text as a
                      tooltip. */}
                  {e.back && (
                    <span className={`lnd-flag ${e.back.certain ? 'sure' : 'maybe'}`}
                      title={e.back.certain
                        ? 'This item is back in your inventory — a float match confirms it.'
                        : 'An item matching this one is in your inventory, but the float does not confirm it.'}>
                      {e.back.certain ? 'back in your inventory' : 'looks like it is back'}
                    </span>
                  )}
                  {over && <span className="lnd-flag late" title={`${Math.abs(due)} days overdue`}>{Math.abs(due)}d overdue</span>}
                  {!over && due != null && <span className="lnd-flag" title={`Due back in ${due} days`}>due in {due}d</span>}
                </div>
                <button className="lnd-btn primary"
                  onClick={() => onMarkReturned && onMarkReturned(e.realIndex, e.back?.item?.assetId || null)}>
                  Mark returned
                </button>
                <button className="lnd-btn danger"
                  onClick={() => onWriteOff && onWriteOff(e.realIndex)}>
                  Write off
                </button>
              </div>
            )}
          </motion.div>
        );
      })}

      {detail && (
        <ItemDetailModal
          stickerIndex={stickerIndex}
          variant="classic"
          kind="lend"
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          lend={detail.lend}
          lentPrice={detail.then}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
